import {
  DECISION_LABEL,
  DECISION_OUTCOMES,
  type DecisionOutcome,
  type DecisionSuggestion,
  type NewCondition,
} from '@cyberscore/contracts';
import { Gavel, Lightbulb, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../ui/Toasts';
import { useDecide, useDecisions } from './api';
import { ReviewStepsCard } from './ReviewSteps';

const HINT: Record<DecisionOutcome, string> = {
  accept: 'Cover on the answers as they stand.',
  accept_with_conditions:
    'Cover once the business meets dated conditions. Each is reminded before it is due.',
  refer: 'Pass it to a senior underwriter. It stays in review, and the business isn’t told.',
  decline: 'No cover on this assessment.',
};

/** A date some weeks out, as YYYY-MM-DD. */
export function inWeeks(n: number, from = new Date()): string {
  const d = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate()));
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
}

const blank = (): NewCondition => ({ title: '', due_date: inWeeks(4) });

/**
 * The underwriter's decision on a reviewed assessment (task 64). It arrives filled in with the suggested
 * decision (task 108, decision 0074), and says why: the underwriter checks it, changes what they want,
 * and records it. Nothing is recorded without them.
 */
export function DecidePanel({
  id,
  blocked,
  unreviewed = 0,
  suggestion = null,
}: {
  id: string;
  blocked: string | null;
  unreviewed?: number;
  suggestion?: DecisionSuggestion | null;
}) {
  const decide = useDecide(id);
  const steps = useDecisions(id).data?.steps;
  const toast = useToast();
  const [outcome, setOutcome] = useState<DecisionOutcome | null>(null);
  const [reason, setReason] = useState('');
  const [conditions, setConditions] = useState<NewCondition[]>([blank()]);
  const [error, setError] = useState<string | null>(null);
  // Fill the form with the suggestion once it loads, unless the underwriter has started on their own.
  const [filled, setFilled] = useState(false);
  const apply = (s: DecisionSuggestion) => {
    setOutcome(s.outcome);
    setReason(s.reason);
    setConditions(s.conditions.length ? s.conditions.map((c) => ({ title: c.title, control: c.control, due_date: inWeeks(c.weeks) })) : [blank()]); // prettier-ignore
  };
  if (suggestion && !filled && !outcome && !reason) {
    // Set while rendering, as React suggests for state that follows a prop: no extra render pass.
    setFilled(true);
    apply(suggestion);
  }
  const asSuggested = !!suggestion && outcome === suggestion.outcome && reason === suggestion.reason;
  const withConditions = outcome === 'accept_with_conditions';
  const ready =
    outcome && reason.trim() && (!withConditions || conditions.every((c) => c.title.trim() && c.due_date));
  const set = (i: number, patch: Partial<NewCondition>) =>
    setConditions(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  // Task 80: with two sign-offs, a cover decision is proposed for a second underwriter to confirm.
  const proposes = !!outcome && outcome !== 'refer' && (steps?.sign_offs ?? 1) > 1;
  const waiting = steps?.proposal ? 'A decision is waiting for a second underwriter (above).' : null;

  return (
    <>
    {steps && <ReviewStepsCard id={id} steps={steps} canWrite />}
    <section className="card pad decide" aria-labelledby={`decide-${id}`}>
      <h2 id={`decide-${id}`} className="section-title row gap">
        <Gavel size={17} /> Decide
      </h2>
      {blocked || waiting ? (
        <p className="muted small">{blocked ?? waiting}</p>
      ) : (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!ready) return;
            setError(null);
            try {
              await decide.mutateAsync({
                outcome,
                reason: reason.trim(),
                conditions: withConditions
                  ? conditions.map((c) => ({ title: c.title.trim(), due_date: c.due_date, ...(c.detail?.trim() ? { detail: c.detail.trim() } : {}), ...(c.control?.trim() ? { control: c.control.trim().toUpperCase() } : {}) })) // prettier-ignore
                  : [],
              });
              toast(outcome === 'refer' ? 'Referred' : proposes ? 'Proposed for a second underwriter' : 'Decision recorded', 'ok');
              setOutcome(null);
              setReason('');
              setFilled(true);
            } catch (err) {
              setError(err instanceof ApiError ? (err.issues[0]?.message ? `${err.message}: ${err.issues[0].message}` : err.message) : 'Could not record the decision'); // prettier-ignore
            }
          }}
        >
          {suggestion && (
            <div className="suggested" role="note">
              <div className="row-between wrap gap">
                <b className="row gap"><Lightbulb size={15} /> Suggested: {DECISION_LABEL[suggestion.outcome]}</b>
                {!asSuggested && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => apply(suggestion)}>
                    Use the suggestion
                  </button>
                )}
              </div>
              <ul className="muted small">
                {suggestion.why.map((w) => <li key={w}>{w}</li>)}
              </ul>
              <p className="muted small">Check it and record it, or choose another outcome. Your decision is what counts.</p>
            </div>
          )}
          {unreviewed > 0 && (
            <p className="muted small">
              {unreviewed} {unreviewed === 1 ? 'answer is' : 'answers are'} not reviewed yet. You can still decide.
            </p>
          )}
          <fieldset className="outcomes">
            <legend className="small">Outcome</legend>
            {DECISION_OUTCOMES.map((o) => (
              <label key={o} className={`outcome ${outcome === o ? 'on' : ''}`}>
                <input type="radio" name={`outcome-${id}`} value={o} checked={outcome === o} onChange={() => setOutcome(o)} />
                <span>
                  <b>{DECISION_LABEL[o]}</b>
                  <span className="muted small">{HINT[o]}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {withConditions && (
            <div className="stack-sm">
              <div className="small"><b>Conditions</b> <span className="muted">— the business sees these</span></div>
              {conditions.map((c, i) => (
                <div key={i} className="condition-edit">
                  <input className="input" aria-label={`Condition ${i + 1}`} placeholder="e.g. Turn on MFA for email and remote access" maxLength={200} value={c.title} onChange={(e) => set(i, { title: e.target.value })} />
                  <div className="row gap">
                    <label className="small grow">
                      Due by
                      <input className="input" type="date" aria-label={`Condition ${i + 1} due date`} value={c.due_date} onChange={(e) => set(i, { due_date: e.target.value })} />
                    </label>
                    <label className="small">
                      Control (optional)
                      <input className="input" aria-label={`Condition ${i + 1} control`} placeholder="IA-01" maxLength={7} value={c.control ?? ''} onChange={(e) => set(i, { control: e.target.value })} />
                    </label>
                    {conditions.length > 1 && (
                      <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove condition ${i + 1}`} onClick={() => setConditions(conditions.filter((_, j) => j !== i))}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {conditions.length < 20 && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setConditions([...conditions, blank()])}>
                  <Plus size={13} /> Add a condition
                </button>
              )}
            </div>
          )}

          <label className="small" htmlFor={`reason-${id}`}>
            Reason, for your file (the business doesn’t see it)
          </label>
          <textarea id={`reason-${id}`} className="input" rows={3} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
          {error && <div className="notice" role="alert">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={!ready || decide.isPending}>
            {outcome ? `${proposes ? 'Propose' : 'Record'}: ${DECISION_LABEL[outcome]}` : 'Choose an outcome'}
          </button>
        </form>
      )}
    </section>
    </>
  ); // prettier-ignore
}
